import { useLayoutEffect, useRef, useState } from 'react';
import type {
  JeopardyClueMode,
  JeopardySharedCell,
  JeopardySharedColumn,
} from '@gsf/games';

/** Where new tiles go. Three shapes, not a nullable field, so no case gets forgotten. */
export type AddTarget =
  /** Append one clue to a column that already exists. A column's foot `+`, and a row's `+` when there is a column to grow into. */
  | { readonly kind: 'append'; readonly categoryId: string; readonly points: number }
  /** A new column at the far right, one clue per entry. A row's `+` past the last column, and "Add column". */
  | { readonly kind: 'column'; readonly points: readonly number[] }
  /**
   * One clue on the foot of every column ("Add row"). Points are per column: on a ragged
   * board a short column's new tile lands higher and is worth that row's value.
   */
  | {
      readonly kind: 'row';
      readonly tiles: readonly { readonly categoryId: string; readonly points: number }[];
    };

/**
 * What to take off the board. Remove means off this board, not out of the bank; only a
 * never-written tile is deleted, so none of these needs a confirmation.
 */
export type RemoveTarget =
  | { readonly kind: 'clue'; readonly clueId: string; readonly categoryId: string }
  | { readonly kind: 'column'; readonly categoryId: string }
  /** One row, across every column that has a tile on it. */
  | { readonly kind: 'row'; readonly clueIds: readonly string[] };

interface Props {
  board: readonly JeopardySharedColumn[];
  /** Omit to render a read-only board (the TV view without control). */
  onPick?: (clueId: string) => void;
  /** Host only: put a played clue back into play. */
  onReopen?: (clueId: string) => void;
  /** Host only: open the inline clue editor. */
  onEdit?: (clueId: string) => void;
  /** Host only: lay out more board. */
  onAdd?: (target: AddTarget) => void;
  /** Host only: take a tile, a column or a row back off the board. */
  onRemove?: (target: RemoveTarget) => void;
  /** Host only: name (or rename) a column. Draws the ghost heading a new column gets. */
  onNameColumn?: (categoryId: string, name: string) => void;
  currentClueId?: string | null;
  /** Extra class for the TV's bigger type. */
  className?: string;
  /** Hover hint on a playable tile — what a click is about to do. */
  pickTitle?: string;
  /**
   * Edit mode: a click anywhere on a tile opens the clue editor (via `onEdit`) instead of
   * putting it in play, and every tile is clickable — including drafts and spent ones.
   */
  editMode?: boolean;
}

/** A layout limit, not a pack limit: 26 columns is unreadable across a living room. */
export const MAX_COLUMNS = 25;
export const MAX_ROWS = 25;

/** Rows shown at once before the board pages. Matches the row cap: a normal board never pages. */
export const PAGE_ROWS = 25;

/**
 * Tile size bounds. Past them the host's board scrolls rather than shrinking tiles
 * nobody can hit; the TV never grows a board and never scrolls.
 */
const CELL_MIN_WIDTH = 3.6; // rem
const CELL_MIN_HEIGHT = 2.1; // rem
const CELL_MAX_WIDTH = 9; // rem — what a 3-column board would otherwise stretch to
const CELL_MAX_HEIGHT = 3.9; // rem

/** Short badge so the host can spot special clues at a glance. */
function modeBadge(mode: JeopardyClueMode): string | null {
  if (mode === 'open') return 'OPEN';
  if (mode === 'daily_double') return 'DD';
  return null;
}

/**
 * What the next tile down is probably worth. Columns climb by a fixed step, so
 * carry on with the one this column is already using rather than guessing a
 * round number — a 200/400/600 column should offer 800, not 700.
 */
function nextPoints(cells: readonly JeopardySharedCell[]): number {
  const last = cells[cells.length - 1]?.points;
  if (last === undefined) return 100;
  const previous = cells[cells.length - 2]?.points;
  const step = previous === undefined ? last : last - previous;
  return last + (step > 0 ? step : 100);
}

/**
 * What a tile in row `row` should be worth: matches the row across the board, falling
 * back to carrying the column on.
 */
function pointsForRow(board: readonly JeopardySharedColumn[], row: number): number {
  for (const column of board) {
    const cell = column.cells[row];
    if (cell) return cell.points;
  }
  const tallest = board.reduce<readonly JeopardySharedCell[]>(
    (best, column) => (column.cells.length > best.length ? column.cells : best),
    [],
  );
  return nextPoints(tallest.slice(0, row));
}

/**
 * The category grid. One CSS grid with explicit rows/columns, so rows line up across
 * ragged columns. A row's `+` targets the column right of that row's last tile, creating
 * one if needed; columns stay contiguous from row 0, so it cannot leave a hole.
 */
export function BoardGrid({
  board,
  onPick,
  onReopen,
  onEdit,
  onAdd,
  onRemove,
  onNameColumn,
  currentClueId,
  className,
  pickTitle,
  editMode = false,
}: Props) {
  const isHost = Boolean(onReopen || onEdit);
  const editable = Boolean(onAdd);

  // Row pagination for oversized boards (e.g. an imported bank of thousands of clues). A
  // normal board (≤ PAGE_ROWS rows) is never paginated, so it renders exactly as before.
  const [rowPage, setRowPage] = useState(0);

  if (board.length === 0 && !editable) {
    return <p className="muted">No clues on the board for this round yet.</p>;
  }

  const rows = board.reduce((most, column) => Math.max(most, column.cells.length), 0);
  const atColumnCap = board.length >= MAX_COLUMNS;
  const atRowCap = rows >= MAX_ROWS;

  const paginated = rows > PAGE_ROWS;
  const pageCount = paginated ? Math.ceil(rows / PAGE_ROWS) : 1;
  const page = Math.min(rowPage, pageCount - 1);
  const winStart = page * PAGE_ROWS;
  const winEnd = paginated ? Math.min(winStart + PAGE_ROWS, rows) : rows;
  const winRows = winEnd - winStart;
  /** A cell's grid row, offset so the visible window starts just under the headings. */
  const gridRowOf = (rowAt: number): number => rowAt - winStart + 2;
  const inWindow = (rowAt: number): boolean => !paginated || (rowAt >= winStart && rowAt < winEnd);

  /**
   * A host gets extra tracks (per-row strip, Add column, feet, Add row) so no two
   * affordances share a cell. The size floor is host-only: the TV must never scroll.
   */
  const columnCount = Math.max(board.length, 1);
  const style = {
    '--board-cols': String(columnCount),
    ...(editable
      ? {
          '--board-cell-w': `${clamp(CELL_MAX_WIDTH * (4 / columnCount), CELL_MIN_WIDTH, CELL_MAX_WIDTH)}rem`,
          '--board-cell-h': `${clamp(CELL_MAX_HEIGHT * (5 / Math.max(winRows, 1)), CELL_MIN_HEIGHT, CELL_MAX_HEIGHT)}rem`,
        }
      : {}),
  } as React.CSSProperties;

  const rightStrip = board.length + 1;
  const feetRow = rows + 2;

  /** The column to the right of row `row`'s last tile — null when there is none yet. */
  function nextColumnOn(row: number): JeopardySharedColumn | null {
    let last = -1;
    board.forEach((column, at) => {
      if (column.cells[row]) last = at;
    });
    return board[last + 1] ?? null;
  }

  return (
    <>
      {paginated ? (
        <div className="row board-pager" style={{ alignItems: 'center', marginBottom: '.4rem' }}>
          <span className="faint">
            Rows {winStart + 1}–{winEnd} of {rows}
          </span>
          <span className="spacer" />
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            disabled={page === 0}
            onClick={() => setRowPage(page - 1)}
          >
            ‹ Prev rows
          </button>
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            disabled={page >= pageCount - 1}
            onClick={() => setRowPage(page + 1)}
          >
            Next rows ›
          </button>
        </div>
      ) : null}
      <div
        className={`board-grid${editable ? ' is-editable' : ''}${className ? ` ${className}` : ''}`}
        style={style}
      >
      {board.map((column, columnAt) => (
        <ColumnHeading
          key={`cat-${column.categoryId}`}
          column={column}
          columnAt={columnAt}
          onNameColumn={onNameColumn}
          onRemove={onRemove}
        />
      ))}

      {board.map((column, columnAt) =>
        column.cells.map((cell, rowAt) => {
          if (!inWindow(rowAt)) return null;
          const badge = modeBadge(cell.mode);
          const done = cell.state === 'done';
          // A tile nobody has written. The TV shows it too; the reducer refuses to play it.
          const draft = cell.draft;

          return (
            <div
              className="board-cell-wrap"
              key={cell.clueId}
              style={{ gridColumn: columnAt + 1, gridRow: gridRowOf(rowAt) }}
            >
              <button
                className={[
                  'board-cell',
                  done ? (isHost ? 'is-done-host' : 'is-done') : '',
                  draft ? 'is-draft' : '',
                  cell.clueId === currentClueId ? 'is-in-play' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                disabled={editMode ? false : draft || (done ? !onReopen : !onPick)}
                title={
                  editMode
                    ? draft
                      ? 'Write this clue'
                      : 'Edit this clue'
                    : draft
                      ? isHost
                        ? 'Nothing written here yet — switch to Edit mode to write it'
                        : undefined
                      : done
                        ? onReopen
                          ? 'Already played — click to put it back in play'
                          : undefined
                        : onPick
                          ? pickTitle
                          : undefined
                }
                aria-label={
                  editMode
                    ? `${draft ? 'Write' : 'Edit'} the ${cell.points} clue in ${
                        column.name || `column ${columnAt + 1}`
                      }`
                    : undefined
                }
                onClick={() =>
                  editMode
                    ? onEdit?.(cell.clueId)
                    : done
                      ? onReopen?.(cell.clueId)
                      : onPick?.(cell.clueId)
                }
              >
                {done && !isHost ? (
                  ''
                ) : cell.label !== null ? (
                  <FitLabel text={cell.label} />
                ) : (
                  cell.points
                )}
                {/* The TV must not spoil which tile is the daily double. */}
                {badge && isHost ? <span className="cell-mode">{badge}</span> : null}
                {done && isHost ? <span className="cell-replay">↻</span> : null}
              </button>

              {/* On hover and focus only: destructive. */}
              {onRemove ? (
                <button
                  className="cell-remove"
                  title={draft ? 'Remove this tile' : 'Take this clue off the board'}
                  aria-label={`Remove the ${cell.points} tile in ${
                    column.name || `column ${columnAt + 1}`
                  }`}
                  onClick={() =>
                    onRemove({
                      kind: 'clue',
                      clueId: cell.clueId,
                      categoryId: column.categoryId,
                    })
                  }
                >
                  ×
                </button>
              ) : null}
            </div>
          );
        }),
      )}

      {editable && !paginated
        ? board.map((column, columnAt) => (
            <button
              key={`down-${column.categoryId}`}
              className="board-cell board-cell-add"
              style={{ gridColumn: columnAt + 1, gridRow: Math.max(column.cells.length + 2, 2) }}
              disabled={column.cells.length >= MAX_ROWS}
              title={
                column.cells.length >= MAX_ROWS
                  ? `A column holds at most ${MAX_ROWS} questions.`
                  : `Add a question below${column.name ? ` in ${column.name}` : ''}`
              }
              aria-label={`Add a question below in ${column.name || `column ${columnAt + 1}`}`}
              onClick={() =>
                onAdd?.({
                  kind: 'append',
                  categoryId: column.categoryId,
                  points: nextPoints(column.cells),
                })
              }
            >
              +
            </button>
          ))
        : null}

      {editable && !paginated
        ? Array.from({ length: rows }, (_, rowAt) => {
            const target = nextColumnOn(rowAt);
            const blocked = target === null && atColumnCap;
            const onRow = board.flatMap((column) => {
              const cell = column.cells[rowAt];
              return cell ? [cell.clueId] : [];
            });
            return (
              <div className="board-strip" key={`strip-${rowAt}`} style={{ gridColumn: rightStrip, gridRow: rowAt + 2 }}>
                <button
                  className="board-cell board-cell-add"
                  disabled={blocked}
                  title={
                    blocked
                      ? `A board holds at most ${MAX_COLUMNS} columns.`
                      : 'Add a question to the right'
                  }
                  aria-label={`Add a question to the right of row ${rowAt + 1}`}
                  onClick={() =>
                    onAdd?.(
                      target
                        ? { kind: 'append', categoryId: target.categoryId, points: pointsForRow(board, rowAt) }
                        : { kind: 'column', points: [pointsForRow(board, rowAt)] },
                    )
                  }
                >
                  +
                </button>
                {onRemove ? (
                  <button
                    className="board-cell board-cell-add is-remove"
                    title={`Take row ${rowAt + 1} off the board — ${onRow.length} tile${
                      onRow.length === 1 ? '' : 's'
                    }`}
                    aria-label={`Remove row ${rowAt + 1}`}
                    onClick={() => onRemove({ kind: 'row', clueIds: onRow })}
                  >
                    ×
                  </button>
                ) : null}
              </div>
            );
          })
        : null}

      {/* Own tracks, so they never collide with the per-row and per-column ones. */}
      {editable && !paginated && board.length > 0 ? (
        <>
          <button
            className="board-edge board-edge-col"
            style={{ gridColumn: rightStrip + 1, gridRow: `1 / span ${feetRow}` }}
            disabled={atColumnCap}
            title={
              atColumnCap
                ? `A board holds at most ${MAX_COLUMNS} columns.`
                : `Add a column of ${rows} question${rows === 1 ? '' : 's'}`
            }
            onClick={() =>
              onAdd?.({
                kind: 'column',
                // Row by row, so a new column's values line up with the ones beside it.
                points: Array.from({ length: rows }, (_, at) => pointsForRow(board, at)),
              })
            }
          >
            <span>Add column</span>
          </button>

          <button
            className="board-edge board-edge-row"
            // Stops at the board's own columns, not under the per-row strip.
            style={{ gridColumn: `1 / span ${board.length}`, gridRow: feetRow + 1 }}
            disabled={atRowCap}
            title={
              atRowCap
                ? `A board holds at most ${MAX_ROWS} rows.`
                : `Add a row across all ${board.length} column${board.length === 1 ? '' : 's'}`
            }
            onClick={() =>
              onAdd?.({
                kind: 'row',
                tiles: board
                  .filter((column) => column.cells.length < MAX_ROWS)
                  .map((column) => ({
                    categoryId: column.categoryId,
                    // Where *this* column's tile will land, which on a ragged board is
                    // not the bottom row.
                    points: pointsForRow(board, column.cells.length),
                  })),
              })
            }
          >
            Add row
          </button>
        </>
      ) : null}

      {/* Empty round: one tile; pressing it gives a 1 × 1 board. */}
      {editable && board.length === 0 ? (
        <button
          className="board-cell board-cell-add board-cell-first"
          style={{ gridColumn: 1, gridRow: 2 }}
          onClick={() => onAdd?.({ kind: 'column', points: [100] })}
        >
          + Add the first question
        </button>
      ) : null}
      </div>
    </>
  );
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/** Tile-face font bounds, px: a custom label grows to fill its tile, then shrinks (wrapping
 * across lines) whenever the text is long or the tile is small, down to a readable floor. */
const LABEL_MIN_PX = 12;
const LABEL_MAX_PX = 56;

/**
 * A custom tile face, sized to fill its tile. Binary-searches the font on the real rendered
 * size — same idea as the clue box in `display.tsx` — and re-fits when the board reflows.
 *
 * The label is absolutely positioned (filling the tile), so changing its font can never
 * resize the tile: without that, an in-flow label feeds its own growth back through the
 * ResizeObserver and the whole board collapses.
 */
function FitLabel({ text }: { text: string }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const el = textRef.current;
    if (!box || !el) return;

    const fit = () => {
      const w = box.clientWidth;
      const h = box.clientHeight;
      if (w <= 0 || h <= 0) return;
      let lo = LABEL_MIN_PX;
      let hi = LABEL_MAX_PX;
      for (let i = 0; i < 8; i += 1) {
        const mid = (lo + hi) / 2;
        el.style.fontSize = `${mid}px`;
        if (el.scrollWidth <= w && el.scrollHeight <= h) lo = mid;
        else hi = mid;
      }
      el.style.fontSize = `${lo}px`;
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, [text]);

  return (
    <div className="cell-label" ref={boxRef}>
      <span ref={textRef}>{text}</span>
    </div>
  );
}

/** A column heading; on the host's board, an input that commits on blur and Enter. */
function ColumnHeading({
  column,
  columnAt,
  onNameColumn,
  onRemove,
}: {
  column: JeopardySharedColumn;
  columnAt: number;
  onNameColumn?: (categoryId: string, name: string) => void;
  onRemove?: (target: RemoveTarget) => void;
}) {
  const style = { gridColumn: columnAt + 1, gridRow: 1 };

  if (!onNameColumn) {
    return (
      <div className="board-cat" style={style}>
        {column.name}
      </div>
    );
  }

  return (
    <div className={`board-cat${column.name ? '' : ' is-unnamed'}`} style={style}>
      {onRemove ? (
        <button
          className="cat-remove"
          title={`Take this column off the board${
            column.cells.length > 1 ? ` — all ${column.cells.length} tiles` : ''
          }`}
          aria-label={`Remove column ${columnAt + 1}${column.name ? `, ${column.name}` : ''}`}
          onClick={() => onRemove({ kind: 'column', categoryId: column.categoryId })}
        >
          ×
        </button>
      ) : null}
      <input
        className="board-cat-input"
        defaultValue={column.name}
        placeholder="Name it"
        aria-label={`Name for column ${columnAt + 1}`}
        maxLength={40}
        key={column.name}
        onBlur={(event) => {
          const next = event.target.value.trim();
          if (next !== column.name) onNameColumn(column.categoryId, next);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') {
            event.currentTarget.value = column.name;
            event.currentTarget.blur();
          }
        }}
      />
    </div>
  );
}
